import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dispose, getSupportedOverlays, init } from "klinecharts";
import { API_BASE_URL } from "../../../config/api";

const TIMEFRAMES = [
  { value: "1m", label: "1m", period: { type: "minute", span: 1 } },
  { value: "5m", label: "5m", period: { type: "minute", span: 5 } },
  { value: "15m", label: "15m", period: { type: "minute", span: 15 } },
  { value: "30m", label: "30m", period: { type: "minute", span: 30 } },
  { value: "1h", label: "1h", period: { type: "hour", span: 1 } },
  { value: "4h", label: "4h", period: { type: "hour", span: 4 } },
  { value: "1d", label: "1d", period: { type: "day", span: 1 } },
];

const CANDLE_TYPES = [
  { value: "candle_solid", label: "Candles" },
  { value: "ohlc", label: "OHLC" },
  { value: "area", label: "Area" },
];

const DRAWING_TOOLS = [
  { value: "cursor", label: "Cursor", mark: "C" },
  { value: "trend", label: "Trend line", mark: "/", overlay: "segment" },
  {
    value: "straight",
    label: "Straight line",
    mark: "\\",
    overlay: "straightLine",
  },
  { value: "ray", label: "Ray", mark: "R", overlay: "rayLine" },
  {
    value: "horizontal",
    label: "Horizontal line",
    mark: "-",
    overlay: "horizontalStraightLine",
  },
  {
    value: "vertical",
    label: "Vertical line",
    mark: "|",
    overlay: "verticalStraightLine",
  },
  { value: "price", label: "Price line", mark: "P", overlay: "priceLine" },
  { value: "fib", label: "Fibonacci", mark: "F", overlay: "fibonacciLine" },
  { value: "tag", label: "Price tag", mark: "#", overlay: "simpleTag" },
  {
    value: "note",
    label: "Annotation",
    mark: "T",
    overlay: "simpleAnnotation",
  },
  { value: "delete", label: "Delete drawings", mark: "X" },
];

const SUPPORTED_OVERLAYS = new Set(getSupportedOverlays());
const CANDLE_PANE_ID = "candle_pane";

const truthy = (value) => value === true || value === "true" || value === 1;

function normalizeSymbol(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

function parseTimestamp(raw) {
  const source = raw?.open_time || raw?.timestamp || raw?.time;
  if (!source) return NaN;
  if (typeof source === "number") {
    return source > 9999999999 ? source : source * 1000;
  }
  return Date.parse(source);
}

function parseCandle(raw) {
  if (!raw) return null;

  const timestamp = parseTimestamp(raw);
  const open = Number(raw.open);
  const high = Number(raw.high);
  const low = Number(raw.low);
  const close = Number(raw.close);
  const volume = Number(raw.volume || 0);
  const turnover = Number(raw.quote_volume || raw.turnover || 0);

  if (![timestamp, open, high, low, close].every(Number.isFinite)) return null;

  return {
    timestamp,
    open,
    high,
    low,
    close,
    volume: Number.isFinite(volume) ? volume : 0,
    turnover: Number.isFinite(turnover) ? turnover : 0,
    isClosed: truthy(raw.is_closed),
  };
}

function mergeCandles(previous, nextCandles) {
  const byTime = new Map();
  previous.forEach((candle) => byTime.set(candle.timestamp, candle));
  nextCandles.forEach((candle) => byTime.set(candle.timestamp, candle));
  return Array.from(byTime.values()).sort((a, b) => a.timestamp - b.timestamp);
}

function getPeriodForTimeframe(timeframe) {
  return (
    TIMEFRAMES.find((item) => item.value === timeframe)?.period ||
    TIMEFRAMES[0].period
  );
}

function buildKlineStyles({ isDark, showGrid, candleType, showCrosshair }) {
  return {
    grid: {
      show: showGrid,
      horizontal: {
        show: showGrid,
        color: isDark ? "#263743" : "#e2e8f0",
        style: "solid",
        size: 1,
        dashedValue: [2, 2],
      },
      vertical: {
        show: showGrid,
        color: isDark ? "#263743" : "#e2e8f0",
        style: "solid",
        size: 1,
        dashedValue: [2, 2],
      },
    },
    candle: {
      type: candleType,
      bar: {
        upColor: "#14b8a6",
        downColor: "#ef4444",
        noChangeColor: "#94a3b8",
        upBorderColor: "#14b8a6",
        downBorderColor: "#ef4444",
        noChangeBorderColor: "#94a3b8",
        upWickColor: "#14b8a6",
        downWickColor: "#ef4444",
        noChangeWickColor: "#94a3b8",
      },
      area: {
        lineColor: "#38bdf8",
        backgroundColor: [
          { offset: 0, color: "rgba(56, 189, 248, 0.35)" },
          { offset: 1, color: "rgba(56, 189, 248, 0.02)" },
        ],
      },
      priceMark: {
        last: {
          upColor: "#14b8a6",
          downColor: "#d75252",
          noChangeColor: "#94a3b8",
          line: { show: true, style: "dashed", dashedValue: [4, 4], size: 1 },
          text: { show: true, color: "#ffffff", size: 12 },
        },
      },
      tooltip: {
        showRule: "always",
        showType: "standard",
      },
    },
    indicator: {
      ohlc: {
        upColor: "rgba(20, 184, 166, 0.35)",
        downColor: "rgba(239, 68, 68, 0.35)",
        noChangeColor: "rgba(148, 163, 184, 0.35)",
      },
      bars: [
        {
          upColor: "rgba(20, 184, 166, 0.4)",
          downColor: "rgba(239, 68, 68, 0.4)",
          noChangeColor: "rgba(148, 163, 184, 0.35)",
        },
      ],
      lines: [
        { color: "#f59e0b", size: 1, style: "solid" },
        { color: "#38bdf8", size: 1, style: "solid" },
        { color: "#a78bfa", size: 1, style: "solid" },
        { color: "#22c55e", size: 1, style: "solid" },
      ],
    },
    xAxis: {
      axisLine: { color: isDark ? "#31424f" : "#cbd5e1" },
      tickText: {
        color: isDark ? "#a8b3bd" : "#475569",
        family: "Vazir, Inter, sans-serif",
      },
    },
    yAxis: {
      axisLine: { color: isDark ? "#31424f" : "#cbd5e1" },
      tickText: {
        color: isDark ? "#a8b3bd" : "#475569",
        family: "Vazir, Inter, sans-serif",
      },
    },
    separator: {
      color: isDark ? "#31424f" : "#cbd5e1",
      size: 1,
      fill: true,
    },
    crosshair: {
      show: showCrosshair,
      horizontal: {
        show: showCrosshair,
        line: {
          show: showCrosshair,
          color: "#94a3b8",
          style: "dashed",
          size: 1,
          dashedValue: [4, 4],
        },
        text: {
          show: showCrosshair,
          color: "#f8fafc",
          backgroundColor: "#475569",
          size: 12,
        },
      },
      vertical: {
        show: showCrosshair,
        line: {
          show: showCrosshair,
          color: "#94a3b8",
          style: "dashed",
          size: 1,
          dashedValue: [4, 4],
        },
        text: {
          show: showCrosshair,
          color: "#f8fafc",
          backgroundColor: "#475569",
          size: 12,
        },
      },
    },
    overlay: {
      point: {
        color: "#38bdf8",
        borderColor: "#0f172a",
        activeColor: "#f59e0b",
        activeBorderColor: "#111827",
      },
      line: {
        color: "#f59e0b",
        size: 1,
        style: "solid",
        dashedValue: [4, 4],
      },
      text: {
        color: "#f8fafc",
        family: "Vazir, Inter, sans-serif",
        size: 12,
      },
    },
  };
}

function useIsDarkMode() {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark"));
    });

    observer.observe(html, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

export default function KlineTradingView({
  symbol,
  className = "",
  fetchWithAuthRefresh = null,
  marketSocketSession = 0,
  registerMarketSocketHandler = null,
  sendMarketSocketMessage = null,
}) {
  const chartRootRef = useRef(null);
  const chartRef = useRef(null);
  const liveCandleCallbackRef = useRef(null);
  const candlesRef = useRef([]);
  const indicatorIdsRef = useRef({ ma: null, volume: null, macd: null });
  const fetchWithAuthRefreshRef = useRef(fetchWithAuthRefresh);

  const isDark = useIsDarkMode();
  const normalizedSymbol = useMemo(() => normalizeSymbol(symbol), [symbol]);

  const [timeframe, setTimeframe] = useState("1m");
  const [candleType, setCandleType] = useState("candle_solid");
  const [activeTool, setActiveTool] = useState("cursor");
  const [showMa, setShowMa] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const [showMacd, setShowMacd] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showCrosshair, setShowCrosshair] = useState(true);
  const [zoomEnabled, setZoomEnabled] = useState(true);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [candles, setCandles] = useState([]);
  const [status, setStatus] = useState("idle");
  const [statusText, setStatusText] = useState("");
  const [chartReadyKey, setChartReadyKey] = useState(0);

  const period = useMemo(() => getPeriodForTimeframe(timeframe), [timeframe]);
  const styleOptions = useMemo(
    () => buildKlineStyles({ isDark, showGrid, candleType, showCrosshair }),
    [candleType, isDark, showCrosshair, showGrid],
  );
  const styleOptionsRef = useRef(styleOptions);
  const zoomEnabledRef = useRef(zoomEnabled);
  const scrollEnabledRef = useRef(scrollEnabled);

  useEffect(() => {
    fetchWithAuthRefreshRef.current = fetchWithAuthRefresh;
  }, [fetchWithAuthRefresh]);

  const setCandlesState = useCallback((nextCandles) => {
    const limited = nextCandles.slice(-1200);
    candlesRef.current = limited;
    setCandles(limited);
  }, []);

  const loadCandles = useCallback(async () => {
    if (!normalizedSymbol) return [];

    setStatus("loading");
    setStatusText("Loading candles");

    const url = new URL(
      `${API_BASE_URL}trading/symbols/${normalizedSymbol}/candles`,
    );
    url.searchParams.set("timeframe", timeframe);
    url.searchParams.set("limit", "900");

    const response =
      typeof fetchWithAuthRefreshRef.current === "function"
        ? await fetchWithAuthRefreshRef.current(url.toString(), {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          })
        : await fetch(url.toString(), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          });

    if (!response) return [];
    if (!response.ok) {
      throw new Error(`https ${response.status}`);
    }

    const payload = await response.json();
    return (payload?.candles || []).map(parseCandle).filter(Boolean);
  }, [normalizedSymbol, timeframe]);

  const pushCandleUpdates = useCallback(
    (incoming) => {
      const parsed = Array.isArray(incoming)
        ? incoming.map(parseCandle).filter(Boolean)
        : [parseCandle(incoming)].filter(Boolean);

      if (!parsed.length) return;

      const merged = mergeCandles(candlesRef.current, parsed);
      setCandlesState(merged);
      parsed.forEach((candle) => liveCandleCallbackRef.current?.(candle));
      setStatus("ready");
      setStatusText("");
    },
    [setCandlesState],
  );

  const clearIndicators = useCallback((chart) => {
    Object.values(indicatorIdsRef.current).forEach((id) => {
      if (id) {
        chart.removeIndicator({ id });
      }
    });
    indicatorIdsRef.current = { ma: null, volume: null, macd: null };
  }, []);

  const syncIndicators = useCallback(
    (chart = chartRef.current) => {
      if (!chart) return;

      clearIndicators(chart);

      indicatorIdsRef.current = {
        ma: showMa
          ? chart.createIndicator("MA", true, { id: CANDLE_PANE_ID })
          : null,
        volume: showVolume
          ? chart.createIndicator("VOL", false, {
              id: "kline_volume_pane",
              height: 96,
              minHeight: 70,
            })
          : null,
        macd: showMacd
          ? chart.createIndicator("MACD", false, {
              id: "kline_macd_pane",
              height: 96,
              minHeight: 70,
            })
          : null,
      };
    },
    [clearIndicators, showMa, showMacd, showVolume],
  );

  const dataLoader = useMemo(
    () => ({
      getBars: async ({ type, callback }) => {
        if (type !== "init") {
          callback([], { backward: false, forward: false });
          return;
        }

        try {
          const nextCandles = await loadCandles();
          setCandlesState(nextCandles);
          setStatus("ready");
          setStatusText(nextCandles.length ? "" : "No candle data");
          callback(nextCandles, { backward: false, forward: false });
          requestAnimationFrame(() => chartRef.current?.scrollToRealTime(200));
        } catch (err) {
          console.error("Could not load kline candles:", err);
          setCandlesState([]);
          setStatus("error");
          setStatusText("Could not load candles");
          callback([], { backward: false, forward: false });
        }
      },
      subscribeBar: ({ callback }) => {
        liveCandleCallbackRef.current = callback;
      },
      unsubscribeBar: () => {
        liveCandleCallbackRef.current = null;
      },
    }),
    [loadCandles, setCandlesState],
  );

  const handleToolClick = (tool) => {
    const chart = chartRef.current;
    setActiveTool(tool.value);

    if (!chart) return;

    if (tool.value === "delete") {
      chart.removeOverlay();
      setActiveTool("cursor");
      return;
    }

    if (!tool.overlay) return;

    if (!SUPPORTED_OVERLAYS.has(tool.overlay)) {
      console.warn(`KLineCharts overlay is not supported: ${tool.overlay}`);
      return;
    }

    chart.createOverlay({
      name: tool.overlay,
      extendData:
        tool.overlay === "simpleAnnotation"
          ? "Note"
          : tool.overlay === "simpleTag"
            ? "Price"
            : undefined,
    });
  };

  useEffect(() => {
    if (!chartRootRef.current) return undefined;

    const chart = init(chartRootRef.current, {
      locale: "en-US",
      styles: styleOptionsRef.current,
    });

    if (!chart) return undefined;

    chartRef.current = chart;
    chart.setZoomEnabled(zoomEnabledRef.current);
    chart.setScrollEnabled(scrollEnabledRef.current);
    chart.setOffsetRightDistance(40);
    chart.setBarSpace(8);
    chart.setSymbol({
      ticker: normalizedSymbol.toUpperCase() || "SYMBOL",
      pricePrecision: 2,
      volumePrecision: 2,
    });
    chart.setPeriod(period);
    chart.setDataLoader(dataLoader);
    setChartReadyKey((prev) => prev + 1);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartRootRef.current);

    return () => {
      resizeObserver.disconnect();
      clearIndicators(chart);
      dispose(chart);
      chartRef.current = null;
      liveCandleCallbackRef.current = null;
    };
  }, [clearIndicators, dataLoader, normalizedSymbol, period]);

  useEffect(() => {
    styleOptionsRef.current = styleOptions;
    chartRef.current?.setStyles(styleOptions);
  }, [styleOptions]);

  useEffect(() => {
    zoomEnabledRef.current = zoomEnabled;
    chartRef.current?.setZoomEnabled(zoomEnabled);
  }, [zoomEnabled]);

  useEffect(() => {
    scrollEnabledRef.current = scrollEnabled;
    chartRef.current?.setScrollEnabled(scrollEnabled);
  }, [scrollEnabled]);

  useEffect(() => {
    syncIndicators();
  }, [chartReadyKey, syncIndicators]);

  useEffect(() => {
    if (typeof registerMarketSocketHandler !== "function") return undefined;

    return registerMarketSocketHandler((message) => {
      if (message?.type === "candle_snapshot") {
        const snapshot = (message.candles || []).filter(
          (item) =>
            normalizeSymbol(item?.symbol) === normalizedSymbol &&
            item?.timeframe === timeframe,
        );
        pushCandleUpdates(snapshot);
        return;
      }

      if (
        message?.type === "candle_update" &&
        normalizeSymbol(message.symbol) === normalizedSymbol &&
        message.timeframe === timeframe
      ) {
        pushCandleUpdates(message);
      }
    });
  }, [
    normalizedSymbol,
    pushCandleUpdates,
    registerMarketSocketHandler,
    timeframe,
  ]);

  useEffect(() => {
    if (!normalizedSymbol || typeof sendMarketSocketMessage !== "function") {
      return undefined;
    }

    sendMarketSocketMessage({
      type: "subscribe_candles",
      symbol: normalizedSymbol,
      timeframe,
    });

    return () => {
      sendMarketSocketMessage({
        type: "unsubscribe_candles",
        symbol: normalizedSymbol,
        timeframe,
      });
    };
  }, [
    marketSocketSession,
    normalizedSymbol,
    sendMarketSocketMessage,
    timeframe,
  ]);

  const statusLabel = useMemo(() => {
    if (statusText) return statusText;
    if (!candles.length) return "Waiting for candles";
    const last = candles[candles.length - 1];
    return `${normalizedSymbol.toUpperCase()} ${timeframe}  O ${last.open.toFixed(2)}  H ${last.high.toFixed(2)}  L ${last.low.toFixed(2)}  C ${last.close.toFixed(2)}`;
  }, [candles, normalizedSymbol, statusText, timeframe]);

  return (
    <section
      className={`relative w-full overflow-hidden rounded-2xl bg-[#17242d] text-slate-200 ring-1 ring-inset ring-[#2d3d49] ${
        isFullscreen ? "fixed inset-3 z-[90] rounded-lg" : "min-h-[640px]"
      } ${className}`}
    >
      <div className="flex min-h-[640px] flex-col">
        <div className="flex min-h-12 flex-wrap items-center gap-2 border-b border-[#2d3d49] bg-[#111b24] px-3 py-2 text-xs">
          <div className="flex items-center gap-2 rounded-md bg-[#17242d] px-3 py-2 font-semibold text-white">
            {normalizedSymbol.toUpperCase() || "SYMBOL"}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {TIMEFRAMES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setTimeframe(item.value)}
                className={`h-8 rounded-md px-2.5 ${
                  timeframe === item.value
                    ? "bg-cyan-500 text-slate-950"
                    : "bg-[#17242d] text-slate-300 hocus:bg-[#223240]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <select
            value={candleType}
            onChange={(event) => setCandleType(event.target.value)}
            className="h-8 rounded-md border border-[#2d3d49] bg-[#17242d] px-2 text-slate-200 outline-none"
          >
            {CANDLE_TYPES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setShowMa((prev) => !prev)}
            className={`h-8 rounded-md px-2.5 ${
              showMa
                ? "bg-amber-400 text-slate-950"
                : "bg-[#17242d] hocus:bg-[#223240]"
            }`}
          >
            MA
          </button>
          <button
            type="button"
            onClick={() => setShowVolume((prev) => !prev)}
            className={`h-8 rounded-md px-2.5 ${
              showVolume
                ? "bg-cyan-500 text-slate-950"
                : "bg-[#17242d] hocus:bg-[#223240]"
            }`}
          >
            Volume
          </button>
          <button
            type="button"
            onClick={() => setShowMacd((prev) => !prev)}
            className={`h-8 rounded-md px-2.5 ${
              showMacd
                ? "bg-violet-400 text-slate-950"
                : "bg-[#17242d] hocus:bg-[#223240]"
            }`}
          >
            MACD
          </button>
          <button
            type="button"
            onClick={() => setShowSettings((prev) => !prev)}
            className="h-8 rounded-md bg-[#17242d] px-2.5 hocus:bg-[#223240]"
          >
            Settings
          </button>
          <button
            type="button"
            onClick={() => chartRef.current?.scrollToRealTime(200)}
            className="h-8 rounded-md bg-[#17242d] px-2.5 hocus:bg-[#223240]"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => setIsFullscreen((prev) => !prev)}
            className="h-8 rounded-md bg-[#17242d] px-2.5 hocus:bg-[#223240]"
          >
            {isFullscreen ? "Exit" : "Full"}
          </button>

          <div className="ml-auto text-[11px] text-slate-400">
            {statusLabel}
          </div>
        </div>

        {showSettings && (
          <div className="flex flex-wrap gap-2 border-b border-[#2d3d49] bg-[#0f1720] px-3 py-2 text-xs">
            <button
              type="button"
              onClick={() => setShowGrid((prev) => !prev)}
              className={`h-8 rounded-md px-2.5 ${
                showGrid
                  ? "bg-cyan-500 text-slate-950"
                  : "bg-[#17242d] hocus:bg-[#223240]"
              }`}
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setShowCrosshair((prev) => !prev)}
              className={`h-8 rounded-md px-2.5 ${
                showCrosshair
                  ? "bg-cyan-500 text-slate-950"
                  : "bg-[#17242d] hocus:bg-[#223240]"
              }`}
            >
              Crosshair
            </button>
            <button
              type="button"
              onClick={() => setZoomEnabled((prev) => !prev)}
              className={`h-8 rounded-md px-2.5 ${
                zoomEnabled
                  ? "bg-amber-400 text-slate-950"
                  : "bg-[#17242d] hocus:bg-[#223240]"
              }`}
            >
              Zoom
            </button>
            <button
              type="button"
              onClick={() => setScrollEnabled((prev) => !prev)}
              className={`h-8 rounded-md px-2.5 ${
                scrollEnabled
                  ? "bg-amber-400 text-slate-950"
                  : "bg-[#17242d] hocus:bg-[#223240]"
              }`}
            >
              Scroll
            </button>
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          <div className="flex w-12 shrink-0 flex-col items-center gap-2 border-r border-[#2d3d49] bg-[#080f13] py-3">
            {DRAWING_TOOLS.map((item) => (
              <button
                key={item.value}
                type="button"
                title={item.label}
                onClick={() => handleToolClick(item)}
                className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold ${
                  activeTool === item.value
                    ? "bg-cyan-500 text-slate-950"
                    : "bg-transparent text-slate-300 hocus:bg-[#223240]"
                }`}
              >
                {item.mark}
              </button>
            ))}
          </div>

          <div className="relative min-h-[580px] flex-1">
            <div ref={chartRootRef} className="absolute inset-0 bg-[#17242d]" />
            {status === "loading" && (
              <div className="absolute left-4 top-4 rounded-md bg-[#111b24]/90 px-3 py-2 text-xs text-slate-300">
                Loading candles
              </div>
            )}
            {status === "error" && (
              <div className="absolute left-4 top-4 rounded-md bg-red-950/90 px-3 py-2 text-xs text-red-100">
                Could not load candles
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
